import { reportSourceForSelection } from './selectionRestrictions';
import type { ReportExplorerConfiguration, ReportFilter, ReportRequest } from './model';
import {
  ReportDateRangeType,
  ReportDimension,
  ReportMetric,
  ReportTimeGranularity,
  ReportType,
} from './reportEnums';
import { serializeReportExplorerFilters } from './reportFilters';

const dimensionMetrics: Record<ReportDimension, readonly ReportMetric[]> = {
  [ReportDimension.Video]: [ReportMetric.VideoId, ReportMetric.VideoName],
  [ReportDimension.Marketplace]: [ReportMetric.Marketplace],
  [ReportDimension.IiqArm]: [ReportMetric.IiqArm],
  [ReportDimension.Browser]: [ReportMetric.Browser],
  [ReportDimension.Total]: [],
  [ReportDimension.Agency]: [ReportMetric.AgencyName, ReportMetric.AgencyId],
  [ReportDimension.Hour]: [ReportMetric.Hour],
  [ReportDimension.Day]: [ReportMetric.Day],
  [ReportDimension.Month]: [ReportMetric.Month],
  [ReportDimension.Year]: [ReportMetric.Year],
  [ReportDimension.Publisher]: [ReportMetric.PublisherName],
  [ReportDimension.PublisherDomain]: [ReportMetric.Domain],
  [ReportDimension.App]: [ReportMetric.AppBundleId],
  [ReportDimension.BidderCode]: [ReportMetric.BidderCode],
  [ReportDimension.DemandPartner]: [ReportMetric.DemandPartnerName],
  [ReportDimension.DemandPartnerOwner]: [ReportMetric.DemandPartnerOwnerName],
  [ReportDimension.ProductType]: [ReportMetric.ProductType],
  [ReportDimension.ContentType]: [ReportMetric.ContentType],
  [ReportDimension.ContentTitle]: [ReportMetric.ContentTitle],
  [ReportDimension.ContentSeries]: [ReportMetric.ContentSeries],
  [ReportDimension.ContentGenre]: [ReportMetric.ContentGenre],
  [ReportDimension.ContentCat]: [ReportMetric.ContentCat],
  [ReportDimension.ContentRating]: [ReportMetric.ContentRating],
  [ReportDimension.ContentChannelName]: [ReportMetric.ContentChannelName],
  [ReportDimension.ContentNetworkName]: [ReportMetric.ContentNetworkName],
  [ReportDimension.ContentLanguage]: [ReportMetric.ContentLanguage],
  [ReportDimension.ContentLivestream]: [ReportMetric.ContentLivestream],
  [ReportDimension.PublisherPage]: [ReportMetric.Page],
  [ReportDimension.Network]: [ReportMetric.NetworkName],
  [ReportDimension.Xp]: [ReportMetric.Xp],
  [ReportDimension.Xp1]: [ReportMetric.Xp1],
  [ReportDimension.Xp2]: [ReportMetric.Xp2],
  [ReportDimension.Xp3]: [ReportMetric.Xp3],
  [ReportDimension.SchainAsi]: [ReportMetric.SchainAsi],
  [ReportDimension.CrId]: [ReportMetric.CrId],
  [ReportDimension.Adomain]: [ReportMetric.Adomain],
  [ReportDimension.SchainNodeDomain]: [ReportMetric.SchainNodeDomain],
  [ReportDimension.Placement]: [ReportMetric.Placement],
  [ReportDimension.DemandIntegration]: [ReportMetric.DemandIntegration],
  [ReportDimension.Deal]: [ReportMetric.Deal],
  [ReportDimension.AuctionType]: [ReportMetric.AuctionType],
  [ReportDimension.LineItem]: [ReportMetric.LineItem],
  [ReportDimension.Order]: [ReportMetric.Order],
  [ReportDimension.Country]: [ReportMetric.Country],
  [ReportDimension.Region]: [ReportMetric.Region],
  [ReportDimension.DataCenter]: [ReportMetric.DataCenter],
  [ReportDimension.ErrorCode]: [ReportMetric.ErrorCode],
  [ReportDimension.AppStore]: [ReportMetric.AppStoreId, ReportMetric.AppStoreUrl],
  [ReportDimension.Platform]: [ReportMetric.Platform],
  [ReportDimension.DvSegment]: [ReportMetric.DvSegment],
  [ReportDimension.IvtProvider]: [ReportMetric.IvtProvider],
  [ReportDimension.IvtType]: [ReportMetric.IvtType],
  [ReportDimension.Brand]: [ReportMetric.BrandName],
  [ReportDimension.Advertiser]: [ReportMetric.AdvertiserName],
  [ReportDimension.DeviceType]: [ReportMetric.DeviceType],
  [ReportDimension.BidfloorReq]: [ReportMetric.BidfloorReq],
  [ReportDimension.Creative]: [ReportMetric.CreativeName],
  [ReportDimension.LrSegment]: [ReportMetric.LrSegment],
  [ReportDimension.Application]: [ReportMetric.ApplicationName, ReportMetric.AppName],
  [ReportDimension.Omidpn]: [ReportMetric.Omidpn],
  [ReportDimension.Omidpv]: [ReportMetric.Omidpv],
};

export function identityMetricsForDimensions(dimensions: readonly ReportDimension[]) {
  return unique(dimensions.flatMap((dimension) => dimensionMetrics[dimension]));
}

export function displayMetricsForDimensions(dimensions: readonly ReportDimension[]) {
  if (!dimensions.some(isContextualReportDimension)) {
    return unique(dimensions.flatMap((dimension) => dimensionMetrics[dimension]));
  }
  return unique(
    dimensions.flatMap(
      (dimension) =>
        contextualDisplayMetrics[dimension] ?? contextualRequestIdentityMetrics[dimension] ?? [],
    ),
  );
}

export function reportDisplayMetrics(request: ReportRequest) {
  return unique([...displayMetricsForDimensions(request.dimensions), ...request.metrics]);
}

export function chartDimensionsForGranularity(granularity: ReportTimeGranularity) {
  switch (granularity) {
    case ReportTimeGranularity.Hour:
      return [ReportDimension.Day, ReportDimension.Hour];
    case ReportTimeGranularity.Day:
      return [ReportDimension.Day];
    case ReportTimeGranularity.Month:
      return [ReportDimension.Year, ReportDimension.Month];
    case ReportTimeGranularity.Year:
      return [ReportDimension.Year];
  }
}

export function reportDateRangeSupportsHour(dateFrom: string, dateTo: string) {
  return dateFrom !== '' && dateFrom === dateTo;
}

export function buildTableReportRequest(
  config: ReportExplorerConfiguration,
  publisherId?: string,
): ReportRequest {
  const source = reportSourceForSelection(config.tableDimensions, config.tableMetrics);
  return {
    dimensions: config.tableDimensions,
    metrics: unique([
      ...(source === ReportType.IntentIq
        ? []
        : requestIdentityMetricsForDimensions(config.tableDimensions)),
      ...config.tableMetrics,
    ]),
    filters: commonFilter(config, publisherId),
    source,
  };
}

function requestIdentityMetricsForDimensions(dimensions: readonly ReportDimension[]) {
  if (!dimensions.some(isContextualReportDimension)) {
    return unique(dimensions.flatMap((dimension) => dimensionMetrics[dimension]));
  }
  return unique(
    dimensions.flatMap((dimension) => contextualRequestIdentityMetrics[dimension] ?? []),
  );
}

export const contextualReportDimensions = [
  ReportDimension.ContentType,
  ReportDimension.ContentTitle,
  ReportDimension.ContentSeries,
  ReportDimension.ContentGenre,
  ReportDimension.ContentCat,
  ReportDimension.ContentRating,
  ReportDimension.ContentChannelName,
  ReportDimension.ContentNetworkName,
  ReportDimension.ContentLanguage,
  ReportDimension.ContentLivestream,
] as const satisfies readonly ReportDimension[];

const contextualDimensions = new Set<ReportDimension>(contextualReportDimensions);

export function isContextualReportDimension(dimension: ReportDimension) {
  return contextualDimensions.has(dimension);
}

const contextualRequestIdentityMetrics: Partial<Record<ReportDimension, readonly ReportMetric[]>> =
  {
    [ReportDimension.Hour]: [ReportMetric.Hour],
    [ReportDimension.Day]: [ReportMetric.Day],
    [ReportDimension.Network]: [ReportMetric.NetworkId],
    [ReportDimension.Agency]: [ReportMetric.AgencyName, ReportMetric.AgencyId],
    [ReportDimension.BidderCode]: [ReportMetric.BidderCode],
    [ReportDimension.DemandIntegration]: [ReportMetric.DemandIntegrationId],
    [ReportDimension.DemandPartner]: [ReportMetric.DemandPartnerName],
    [ReportDimension.DemandPartnerOwner]: [ReportMetric.DemandPartnerOwnerName],
    [ReportDimension.ContentTitle]: [ReportMetric.ContentTitle],
    [ReportDimension.ContentSeries]: [ReportMetric.ContentSeries],
    [ReportDimension.ContentGenre]: [ReportMetric.ContentGenre],
    [ReportDimension.ContentCat]: [ReportMetric.ContentCat],
    [ReportDimension.ContentRating]: [ReportMetric.ContentRating],
    [ReportDimension.ContentChannelName]: [ReportMetric.ContentChannelName],
    [ReportDimension.ContentNetworkName]: [ReportMetric.ContentNetworkName],
    [ReportDimension.ContentLanguage]: [ReportMetric.ContentLanguage],
    [ReportDimension.ContentLivestream]: [ReportMetric.ContentLivestream],
  };

const contextualDisplayMetrics: Partial<Record<ReportDimension, readonly ReportMetric[]>> = {
  [ReportDimension.Year]: [ReportMetric.Year],
  [ReportDimension.Month]: [ReportMetric.Month],
  [ReportDimension.Publisher]: [ReportMetric.PublisherSecondaryId],
  [ReportDimension.App]: [ReportMetric.AppBundleId],
  [ReportDimension.PublisherDomain]: [ReportMetric.Domain],
  [ReportDimension.Placement]: [ReportMetric.PlacementId],
  [ReportDimension.ContentType]: [ReportMetric.ContentType],
};

export function buildChartReportRequest(
  config: ReportExplorerConfiguration,
  publisherId?: string,
): ReportRequest {
  const dimensions = chartDimensionsForGranularity(config.chartGranularity);
  const source = reportSourceForSelection(dimensions, config.chartMetrics);
  return {
    dimensions,
    metrics: unique([
      ...(source === ReportType.IntentIq ? [] : identityMetricsForDimensions(dimensions)),
      ...config.chartMetrics,
    ]),
    filters: commonFilter(config, publisherId),
    source,
  };
}

export function buildTimeShiftReportRequest(
  request: ReportRequest,
  days: number | null,
): ReportRequest | null {
  if (days === null) return null;
  if (!Number.isSafeInteger(days) || days <= 0) return null;

  return {
    ...request,
    filters: {
      ...request.filters,
      dateRange: null,
      dateFrom: shiftIsoDate(request.filters.dateFrom, -days),
      dateTo: shiftIsoDate(request.filters.dateTo, -days),
    },
  };
}

export function createDefaultReportExplorerConfiguration(
  now = new Date(),
): ReportExplorerConfiguration {
  const end = startOfLocalDay(now);
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  return {
    dateFrom: localDate(start),
    dateTo: localDate(end),
    dateRange: ReportDateRangeType.Last7Days,
    tableDimensions: [ReportDimension.Publisher],
    tableMetrics: [ReportMetric.AdRequests],
    chartGranularity: ReportTimeGranularity.Day,
    chartMetrics: [ReportMetric.AdRequests],
    timeShiftDays: null,
    reportFilters: [],
  };
}

export function createDefaultPublisherReportExplorerConfiguration(now = new Date()) {
  return createDefaultReportExplorerConfiguration(now);
}

export function reportRequestsAreEqual(left: ReportRequest, right: ReportRequest) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function commonFilter(config: ReportExplorerConfiguration, publisherId?: string): ReportFilter {
  return {
    ...config.passthroughFilters,
    dateRange: config.dateRange,
    dateFrom: config.dateFrom,
    dateTo: config.dateTo,
    exchangeNetworkOnly: config.passthroughFilters?.exchangeNetworkOnly ?? false,
    ...serializeReportExplorerFilters(config.reportFilters),
    ...(publisherId ? { publisherIds: [publisherId] } : {}),
  };
}

function unique<T>(values: readonly T[]) {
  return [...new Set(values)];
}

function startOfLocalDay(value: Date) {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function localDate(value: Date) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${String(year)}-${month}-${day}`;
}

function shiftIsoDate(value: string, days: number) {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!parts) return value;
  const [, year, month, day] = parts;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function createDefaultAgencyReportExplorerConfiguration(
  now = new Date(),
): ReportExplorerConfiguration {
  return {
    ...createDefaultReportExplorerConfiguration(now),
    tableDimensions: [ReportDimension.Agency],
    tableMetrics: [
      ReportMetric.AdImpressionsLi,
      ReportMetric.RevenueLi,
      ReportMetric.RpmLi,
      ReportMetric.AdClicks,
    ],
    chartMetrics: [
      ReportMetric.AdImpressionsLi,
      ReportMetric.RevenueLi,
      ReportMetric.RpmLi,
      ReportMetric.AdClicks,
    ],
  };
}
